import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';

import './NumberInput.scss';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from 'src/configs/store';
import UseActiveElement from 'src/hooks/useActiveElement';

interface NumberInputProps {
  selectedMarket: string,
  clear: boolean,
  numberColours?: string,
  lastSelection?:boolean,
  setLastSelection?: () => void,
  setClear: () => void,
  lastValues?: string[],
  shouldListen?: boolean
}

const NumberInput = React.forwardRef(({
  numberColours='BBBBBBBBBBCCCCCCCCCCDDDDDDDDDDEEEEEEEEEEFFFFFFFFF',
  selectedMarket, clear, setClear, lastSelection, setLastSelection, lastValues, shouldListen=true }: NumberInputProps, ref:any) => {
  const [value, setValue] = useState(['']);

  useEffect(() => {
    setValue(lastValues);
  }, [lastValues]);

  const { t } = useTranslation();
  const { usedNumbers } = useSelector((state: RootState) => state.lotto);

  const [numberOfInputs, setNumberOfInputs] = useState(1);
  const activeIndex = useRef(0);
  const dispatch = useDispatch();
  const activeElement = UseActiveElement();

  const clearAll = () => {
    setValue(['']);
    activeIndex.current = 0;
    setNumberOfInputs(1);
  };

  useEffect(() => {
    if (clear){
      clearAll();
      setClear();
    } else if (!_.isEqual(value, [''])) {
      dispatch.lotto.changeUsedNumbers(value);
    }
  }, [clear, value]);

  useEffect(() => {
    if (lastSelection){
      setLastSelection();
      setValue(usedNumbers);
    }
  }, [lastSelection]);

  const backspaceFunction = (e) => {
    if (e.key === 'Backspace' && shouldListen &&
    (!document.activeElement.className.includes('--stake') && !document.activeElement.className.includes('fast-mode__search-input'))){
      if ((value[+activeIndex.current] === '' || !value[+activeIndex.current]) && +activeIndex.current > 0){
        const inputList : any = document.getElementsByClassName('input');
        const focusElement :HTMLElement = inputList[+activeIndex.current - 1];
        focusElement.focus();
      }
      if (value[+activeIndex.current] === '' && +activeIndex.current > 0){
        const inputList : any = document.getElementsByClassName('input');
        const focusElement :HTMLElement = inputList[+activeIndex.current - 1];
        focusElement.focus();
      }
    }

    if (e.key === '.' && value.includes('+')){
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (ref.current){
      addEventListener('keydown', backspaceFunction);
    } else {
      removeEventListener('keydown', backspaceFunction);
    }
    return () => {
      removeEventListener('keydown', backspaceFunction);
    };
  }, [numberOfInputs, value, activeIndex, activeElement]);


  useEffect(() => {
    if (value[+activeIndex.current] && (value[+activeIndex.current].length === 2 || value[+activeIndex.current] === '+')){
      const inputList : any = document.getElementsByClassName('input');
      const focusElement = _.findIndex(inputList, { value: '' });
      if (focusElement >=0){
        inputList[focusElement].focus();
      }
    }

    if (!ref.current){
      const inputList : any = document.getElementsByClassName('input');
      ref.current = inputList[0];
      activeIndex.current = 0;
      setClear();
    }
  }, [value, ref.current]);

  const isAllowed = (value) => {
    if (value == +value || value === '.'){
      return true;
    }
    return false;
  };

  const handleNumbers =(index, e) => {
    e.preventDefault();
    if (isAllowed(e.target.value) && value) {
      const val = e.target.value === '.' ? '+' : e.target.value;
      const oldValues = JSON.parse(JSON.stringify(value));

      if (!oldValues[index] || oldValues[index]?.length <= 2 && val.length <=2){
        oldValues[index] = val;
        setValue(JSON.parse(JSON.stringify(oldValues)));
      }
    }
  };

  useEffect(() => {
    dispatch.lotto.changeValues({ key: 'numbers', value: value.join('') });
    if (value[value.length -1].length === 2 || value[value.length -1]=== '+'){
      setNumberOfInputs(value.length +1);
    }

  }, [value]);

  useEffect(() => {
    const inputList : any = document.getElementsByClassName('input');
    const focusElement :HTMLElement = inputList[inputList.length -1];
    focusElement.focus();
  }, [numberOfInputs]);

  const checkMarket = () => {
    if (_.isEqual(value, [''])){
      return '';
    }

    const newValue = _.filter(value, (val) => val !== '');

    if (newValue[0] === '+'){
      return `${ newValue.length -1 } numbers main`;
    } else if (!newValue.includes('+')){
      return `${ newValue.length } numbers with bonus`;
    } else if (newValue[newValue.length -1] === '+'){
      return `${ newValue.length -1 } bonus`;
    }
    const indexOfBonus = _.indexOf(newValue, '+');
    return `${ indexOfBonus } numbers + ${ newValue.length - (indexOfBonus +1) } bonus`;
  };

  useEffect(() => {
    if (!value || _.isEqual(value, [''])){
      const inputList : any = document.getElementsByClassName('input');
      ref.current = inputList[0];
    }
  }, [value]);

  return (
    <>
      <div className="numbers-input">
        {
          _.map(Array.from({ length: numberOfInputs }, (_, i) => i + 1), (number, index) => {
            if ((value[index] !== '+' && value[index]) || (value.includes('+') && value[index] !== '+')){
              const color = numberColours[parseInt(value[index]) -1]?.toLowerCase() || 'o';
              return (
                <div className={
                  classNames({
                    'numbers-input__ball-container': true,
                    [`numbers-input__ball-container--${ color }`]: value[index] && value[index].length === 2
                  })
                }
                >
                  <input
                    key={ number }
                    autoComplete="off"
                    ref={ ref }
                    maxLength={ 1 }
                    type="number"
                    onFocus={ () => activeIndex.current = index }
                    className={
                      classNames({
                        'input focusable' : true,
                        'numbers-input__plus': value[index] === '+' || (!value[index] && !value.includes('+')),
                        'numbers-input__number':(value[index] !== '+' && value[index] || value.includes('+'))

                      })
                    }
                    name="numbers"
                    value={ value[index] }
                    onChange={ (e) => handleNumbers(index, e) }
                  />
                </div>
              );
            } else {
              return (
                <div className="numbers-input__empty-container">
                  <input
                    key={ number }
                    autoComplete="off"
                    ref={ ref }
                    onFocus={ () => activeIndex.current = index }
                    maxLength={ 1 }
                    className={
                      classNames({
                        'input focusable': true,
                        'numbers-input__plus': value[index] === '+' || !value[index],
                        'numbers-input__number':value[index] !== '+' && value[index]
                      })
                    }
                    name="numbers"
                    value={ value[index] === '+' ? '.' : '' }
                    onChange={ (e) => handleNumbers(index, e) }
                  />
                </div>
              );
            }

          })
        }
      </div>
      <div className="number-input-markets">
        <div className="number-input-markets__markets-container">
          <div className="number-input-markets__title">Markets:</div>
          <span className="number-input-markets__type">{ selectedMarket ? selectedMarket : checkMarket() }</span>
        </div>
        <div className="number-input-markets__buttons-container">
          <button
            className="fast-mode__clear"
            onClick={ clearAll }
            disabled={ _.isEqual(value, ['']) }
          > { t('numbers.clearAll') }</button>
        </div>
      </div>
    </>
  );
});

export default NumberInput;
